/* globals window */
import { Button } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import React, { useContext, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../brainApi';
import { QuickQuoteContext } from '../Context';
import {
  emailValidationRegExp,
  exclusiveOR,
  getZ3PConfigValue,
} from '../helper';
import { EditableQuoteAnswerList } from '../tabs/quotes/QuoteQuestionFields';
import { usStateOptions } from '../UsStates';
import useStyles from './styles';
import {
  commonVehicleColors,
  getVehicleType,
  heavyDutyEngineMakes,
  heavyDutyMakes,
  heavyDutyModels,
  nonautomobileTabs,
  shouldDisplayVehicleType,
} from '../VehicleConstants';
import BetaLabel from '../components/BetaLabel';
import DialogTitle from '../components/DialogTitle';
import { getInitialVehicleTab, getTabNames } from '../vehicleHelper';

const componentStyle = makeStyles(() => ({
  sectionHeader: {
    marginTop: '1em',
  },
  sectionText: {
    color: 'rgba(0, 0, 0, 0.65)',
  },
  isSubmittingQuoteTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '1em',
  },
  isSubmittingQuoteText: {
    marginLeft: 10,
  },
  betaLabel: {
    position: 'absolute',
    top: '4px',
    left: '122px',
  },
}));

const VehicleLabel = ({ vehicle }) => {
  const { display_name, license_plate_number, license_plate_state } = vehicle;
  const type = shouldDisplayVehicleType() && getVehicleType(vehicle);
  const classes = useStyles();
  return (
    <>
      <Typography>{display_name}</Typography>
      {(license_plate_number || license_plate_state) && (
        <Typography className={classes.typography} style={{ fontSize: '12px' }}>
          {[license_plate_number, license_plate_state]
            .filter(Boolean)
            .join(' ')}
        </Typography>
      )}
      {type && (
        <Typography className={classes.typography} style={{ fontSize: '12px' }}>
          {type}
        </Typography>
      )}
    </>
  );
};

const NewVehicle = ({ setVehicle, setCreateNewVehicle = false }) => {
  const classes = useStyles();

  const {
    available_vehicle_entry_tabs: availableVehicleEntryTabs,
    default_vehicle_entry_tab: defaultVehicleEntryTab,
    is_using_heavy_duty: isUsingHeavyDuty,
  } = getZ3PConfigValue();

  const initialTab = getInitialVehicleTab(
    availableVehicleEntryTabs,
    defaultVehicleEntryTab,
  );

  const [tabLabelMap, setTabLabelMap] = useState({});
  const [labelActive, setLabelActive] = useState(initialTab);
  const [licenseData, setLicenseData] = useState({
    license_plate_number: null,
    license_plate_state: null,
    color: null,
  });
  const [ymmtData, setYmmtData] = useState({
    year: null,
    make: null,
    model: null,
    trim: null,
    color: null,
  });
  const [vinData, setVinData] = useState({
    vin: null,
    color: null,
  });
  const [heavyDutyData, setHeavyDutyData] = useState({
    year: null,
    make: null,
    model: null,
    heavy_duty_engine_make: null,
    heavy_duty_engine_serial_number: null,
  });
  const [nonautomobileData, setNonautomobileData] = useState({
    year: null,
    make: null,
    model: null,
    color: null,
  });

  useEffect(() => {
    setTabLabelMap(getTabNames(availableVehicleEntryTabs, isUsingHeavyDuty));
  }, []);

  useEffect(() => {
    // Use this to check if the form is actually valid and set the vehicle
    const nonautomobileIsValid = !!nonautomobileData.make;
    const licenseIsValid = !Object.values(licenseData).some((elem) => !elem);
    const ymmtIsValid = !Object.values(ymmtData).some((elem) => !elem);
    const vinIsValid = !Object.values(vinData).some((elem) => !elem);
    const heavyDutyIsValid = !!heavyDutyData.make;

    if (labelActive === 'license' && licenseIsValid) {
      setVehicle({ ...licenseData, input_method_type: 'license' });
    } else if (labelActive === 'year' && ymmtIsValid) {
      setVehicle({ ...ymmtData, input_method_type: 'year' });
    } else if (labelActive === 'vin' && vinIsValid) {
      setVehicle({ ...vinData, input_method_type: 'vin' });
    } else if (labelActive === 'heavy' && heavyDutyIsValid) {
      setVehicle({ ...heavyDutyData, input_method_type: 'heavy' });
    } else if (nonautomobileIsValid) {
      setVehicle({ ...nonautomobileData, input_method_type: labelActive });
    } else {
      setVehicle(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    labelActive,
    licenseData,
    ymmtData,
    vinData,
    heavyDutyData,
    nonautomobileData,
  ]);

  const changeTabOnClick = (tabName) => {
    setLabelActive(tabName);
  };

  const correctlyPopulateVehicles = (tab, field, val) => {
    switch (tab) {
      case 'license':
        setLicenseData((prevVehicle) => ({ ...prevVehicle, [field]: val }));
        break;
      case 'year':
        setYmmtData((prevVehicle) => ({ ...prevVehicle, [field]: val }));
        break;
      case 'vin':
        setVinData((prevVehicle) => ({ ...prevVehicle, [field]: val }));
        break;
      case 'heavy':
        setHeavyDutyData((prevVehicle) => ({ ...prevVehicle, [field]: val }));
        break;
      default:
        setNonautomobileData((prevVehicle) => ({
          ...prevVehicle,
          [field]: val,
        }));
    }
  };

  const handleTextFieldChange = (e) => {
    const tab = e.target.id.split('-')[0];
    const field = e.target.id.split('-')[1];
    const val = e.target.value;
    correctlyPopulateVehicles(tab, field, val);
  };

  const handleAutocompleteChange = (newValue, tab, field) => {
    const val = newValue?.id;
    correctlyPopulateVehicles(tab, field, val);
  };

  const renderNonautomobileSection = () => (
    <>
      <Grid
        container
        spacing={1}
        className={`${classes.formRoot} ${classes.width100}`}
      >
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={`${labelActive}-year`}
            label="Year"
            value={nonautomobileData.year}
            onChange={handleTextFieldChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={`${labelActive}-make`}
            label="Make"
            value={nonautomobileData.make}
            onChange={handleTextFieldChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={`${labelActive}-model`}
            label="Model"
            value={nonautomobileData.model}
            onChange={handleTextFieldChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            id={`${labelActive}-color`}
            label="Color"
            value={commonVehicleColors.find((el) => el.id === ymmtData.color)}
            options={commonVehicleColors}
            getOptionLabel={(option) => option.id}
            onChange={(e, newValue) =>
              handleAutocompleteChange(newValue, labelActive, 'color')
            }
            renderInput={(params) => <TextField {...params} label="Color" />}
          />
        </Grid>
      </Grid>
    </>
  );

  const renderTabs = () => {
    if (Object.keys(tabLabelMap).length < 2) return null;
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-evenly"
        flexWrap="wrap"
      >
        {Object.entries(tabLabelMap).map(([tab, label]) => (
          <Box
            key={tab}
            className={`${classes.tabLabel} ${
              labelActive === tab ? classes.tabActive : ''
            }`}
            onClick={() => changeTabOnClick(tab)}
          >
            {label}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {renderTabs()}
      <Grid container spacing={1} className={classes.formRoot}>
        {labelActive === 'heavy' && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="heavy-year"
                label="Year"
                value={heavyDutyData.year}
                onChange={handleTextFieldChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="heavy-make"
                value={heavyDutyMakes.find(
                  (el) => el.id === heavyDutyData.make,
                )}
                options={heavyDutyMakes}
                getOptionLabel={(option) => option.id}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(newValue, 'heavy', 'make')
                }
                renderInput={(params) => (
                  <TextField {...params} label="Make" required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="heavy-model"
                value={heavyDutyModels.find(
                  (el) => el.id === heavyDutyData.model,
                )}
                options={heavyDutyModels}
                getOptionLabel={(option) => option.id}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(newValue, 'heavy', 'model')
                }
                renderInput={(params) => (
                  <TextField {...params} label="Model" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="heavy-heavy_duty_engine_make"
                value={heavyDutyEngineMakes.find(
                  (el) => el.id === heavyDutyData.heavy_duty_engine_make,
                )}
                options={heavyDutyEngineMakes}
                getOptionLabel={(option) => option.id}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(
                    newValue,
                    'heavy',
                    'heavy_duty_engine_make',
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} label="Engine Make" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="heavy-heavy_duty_engine_serial_number"
                label="Engine ID"
                value={heavyDutyData.heavy_duty_engine_serial_number}
                onChange={handleTextFieldChange}
              />
            </Grid>
          </>
        )}
        {labelActive === 'license' && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="license-license_plate_number"
                label="License plate"
                value={licenseData.license_plate_number}
                onChange={handleTextFieldChange}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="license-license_plate_state"
                value={usStateOptions.find(
                  (el) => el.id === licenseData.license_plate_state,
                )}
                options={usStateOptions}
                getOptionLabel={(option) => option.name}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(
                    newValue,
                    'license',
                    'license_plate_state',
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} label="License plate state" required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="license-color"
                value={commonVehicleColors.find(
                  (el) => el.id === licenseData.color,
                )}
                options={commonVehicleColors}
                getOptionLabel={(option) => option.name}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(newValue, 'license', 'color')
                }
                renderInput={(params) => (
                  <TextField {...params} label="Color" required />
                )}
              />
            </Grid>
          </>
        )}
        {labelActive === 'year' && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="year-year"
                label="Year"
                value={ymmtData.year}
                onChange={handleTextFieldChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="year-make"
                label="Make"
                value={ymmtData.make}
                onChange={handleTextFieldChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="year-model"
                label="Model"
                value={ymmtData.model}
                onChange={handleTextFieldChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="year-trim"
                label="Trim"
                value={ymmtData.trim}
                onChange={handleTextFieldChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="year-color"
                value={commonVehicleColors.find(
                  (el) => el.id === ymmtData.color,
                )}
                options={commonVehicleColors}
                getOptionLabel={(option) => option.id}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(newValue, 'year', 'color')
                }
                renderInput={(params) => (
                  <TextField {...params} label="Color" required />
                )}
              />
            </Grid>
          </>
        )}
        {labelActive === 'vin' && (
          <Grid
            container
            direction="row"
            justify="space-evenly"
            alignItems="center"
          >
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="vin-vin"
                label="VIN"
                value={vinData.vin}
                onChange={handleTextFieldChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="vin-color"
                options={commonVehicleColors}
                value={commonVehicleColors.find(
                  (el) => el.id === vinData.color,
                )}
                onChange={(e, newValue) =>
                  handleAutocompleteChange(newValue, 'vin', 'color')
                }
                getOptionLabel={(option) => `${option.id}`}
                renderInput={(params) => (
                  <TextField {...params} label="Color" required />
                )}
              />
            </Grid>
          </Grid>
        )}
        {nonautomobileTabs.includes(labelActive) &&
          renderNonautomobileSection()}
      </Grid>

      {setCreateNewVehicle && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <MuiDialogActions>
            <Button
              onClick={() => setCreateNewVehicle(false)}
              startIcon={<ClearIcon />}
            >
              Cancel
            </Button>
          </MuiDialogActions>
        </Box>
      )}
    </>
  );
};

const SelectVehicle = ({
  vehicles,
  value,
  setValue,
  createNewVehicle,
  setCreateNewVehicle,
  setNewVehicle,
}) => {
  const [numToShow, setNumToShow] = useState(3);

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  return (
    <>
      {createNewVehicle ? (
        <NewVehicle
          setVehicle={setNewVehicle}
          setCreateNewVehicle={setCreateNewVehicle}
        />
      ) : (
        <>
          {vehicles ? (
            <div>
              <Grid item xs>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="vehicle"
                    name="vehicle"
                    value={value}
                    onChange={handleChange}
                  >
                    {vehicles.slice(0, numToShow).map((vehicle) => (
                      <FormControlLabel
                        value={`${vehicle.id}`}
                        control={<Radio />}
                        label={<VehicleLabel vehicle={vehicle} />}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs>
                {numToShow < vehicles.length && (
                  <Button
                    startIcon={<ExpandMore />}
                    onClick={() => setNumToShow((prevState) => prevState + 5)}
                  >
                    Show More
                  </Button>
                )}
              </Grid>
              <Grid item xs>
                {setCreateNewVehicle && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCreateNewVehicle(true);
                      setValue(null);
                    }}
                  >
                    Add New
                  </Button>
                )}
              </Grid>
            </div>
          ) : (
            <CircularProgress />
          )}
        </>
      )}
    </>
  );
};

const QuickQuoteLayout = ({ setQuoteURL }) => {
  const { handleClose } = useContext(QuickQuoteContext);

  const classes = useStyles();
  const compClasses = componentStyle();

  // If we wanted to populate data into the modal from outside of  this component we could set this state in the context
  // For now I left it here because I didn't see that need
  const [canSubmit, setCanSubmit] = useState(false);
  const [customerValue, setCustomerValue] = useState(null);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [newEmail, setNewEmail] = useState(null);
  const [errorText, setErrorText] = useState(null);
  const [selectedQuoteTemplate, setSelectedQuoteTemplate] = useState(null);
  const [
    selectedQuoteTemplateAnswers,
    setSelectedQuoteTemplateAnswers,
  ] = useState(null);
  const [answersValid, setAnswersValid] = useState(true);
  const [adminNote, setAdminNote] = useState(null);

  const [vehicles, setVehicles] = useState(null);
  const [selectedVehicleID, setSelectedVehicleID] = useState(null);

  const [newVehicle, setNewVehicle] = useState(null);
  const [createNewVehicle, setCreateNewVehicle] = useState(false);
  const [disableInput, setDisableInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showTemplateFields, setShowTemplateFields] = useState(false);
  const { is_skipping_vehicle: isSkippingVehicle } = getZ3PConfigValue();

  const getExistingCustomers = (email) => {
    const filter = { q: email, limit: 15 };
    const params = { filter: JSON.stringify(filter) };
    apiGet('/customer-search-detail', params).then((customers) => {
      if (customers) {
        setCustomerResults(customers);
      }
    });
  };

  const getTemplates = () => {
    apiGet('/quote-template').then((templates) => {
      // Note: This is currently hardcoded to only show additional template fields if the user has an active "Tire Quote" template
      // Here's a snapshot from before we hardcoded this. Reference this when we want to enable multiple templates:
      // https://github.com/zippitycars/zippity-admin/blob/a002c2f9046bf49d868bee29ecc9fafd06c54b2d/src/QuickActions/QuickQuoteLayout.js
      const tireTemplate = templates.filter((t) => t.name === 'Tire Quote')[0];
      setSelectedQuoteTemplate(tireTemplate);
    });
  };

  const submitQuoteRequest = (withRedirect = false) => {
    setIsSubmitting(true);
    let newVehicleDataWithCustomer = {};
    if (newVehicle) {
      newVehicleDataWithCustomer = {
        ...newVehicle,
        customer_id: existingCustomer?.id,
      };
    }
    apiPost('/quick-quote', {
      customer_id: existingCustomer?.id,
      vehicle_id: selectedVehicleID,
      customer_email: newEmail,
      new_vehicle: newVehicleDataWithCustomer,
      admin_note: adminNote,
      answers: selectedQuoteTemplateAnswers,
    })
      .then((json) => {
        if (json.Status === 'Failure') {
          console.log('Error: ', json?.Message);
          setShowErrorMessage(true);
        } else {
          const { quote_id, quote_request_id } = json;
          const url = `#/quote-builder/?quote_id=${quote_id}&request_id=${quote_request_id}`;
          if (withRedirect) {
            window.open(url);
          } else {
            setQuoteURL(url);
          }
          handleClose();
        }
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    setShowErrorMessage(false);
    const vehicleExists = exclusiveOR(newVehicle, selectedVehicleID);
    const customerExists = exclusiveOR(newEmail, existingCustomer);

    if (
      (vehicleExists || isSkippingVehicle) &&
      customerExists &&
      answersValid
    ) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [newVehicle, selectedVehicleID, existingCustomer, newEmail, answersValid]);

  useEffect(() => {
    getTemplates();
  }, []);

  useEffect(() => {
    if (customerInputValue === '') {
      setExistingCustomer(null);
      setCustomerResults([]);
      setVehicles(null);
      setDisableInput(false);
    } else {
      getExistingCustomers(customerInputValue);
    }
  }, [customerInputValue]);

  useEffect(() => {
    if (customerValue) {
      setExistingCustomer(customerValue);
      setNewEmail(null);
      setDisableInput(true);
      setErrorText(null);
    }
  }, [customerValue]);

  useEffect(() => {
    if (selectedQuoteTemplate) {
      setSelectedQuoteTemplateAnswers(
        selectedQuoteTemplate.questions.map((question) => ({
          answer: null,
          question,
        })),
      );
    } else {
      setSelectedQuoteTemplateAnswers(null);
    }
  }, [selectedQuoteTemplate]);

  useEffect(() => {
    if (existingCustomer) {
      setVehicles(null);
      apiGet(`/customer/${existingCustomer.id}`).then((customerData) => {
        setVehicles(customerData.vehicles);
      });
    }
  }, [existingCustomer]);

  const validateCustomerInput = () => {
    if (
      !customerInputValue.match(emailValidationRegExp()) &&
      customerInputValue !== ''
    ) {
      setErrorText('Enter a valid email address');
      setExistingCustomer(null);
      setNewEmail(null);
    } else {
      setErrorText(null);
      const cust = customerResults.find(
        (obj) => obj.email === customerInputValue,
      );
      if (cust) {
        // Customer obj exists
        setExistingCustomer(cust);
        setNewEmail(null);
        // setDisableEmailField(true);
        setDisableInput(true);
      } else {
        // Customer obj doesn't exist
        setNewEmail(customerInputValue);
        setExistingCustomer(null);
      }
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      aria-labelledby="quote-creator-dialogue"
      open
      className={classes.dialogRoot}
    >
      <DialogTitle onClose={handleClose}>
        Start Quote
        <BetaLabel classAttr={compClasses.betaLabel} />
      </DialogTitle>
      <MuiDialogContent>
        <div style={{ maxWidth: '67%', margin: 'auto' }}>
          <Typography align="left" variant="h5">
            Contact
          </Typography>
          <Autocomplete
            id="customer-input"
            freeSolo
            blurOnSelect
            autoHighlight
            align="center"
            style={{ width: '100%', margin: 'auto' }}
            options={customerResults}
            getOptionLabel={(option) => {
              return option.email ? option.email : customerInputValue;
            }}
            value={customerValue}
            onChange={(event, newValue) => {
              // If the newValue has an email associated, then it is an existing customer
              // Otherwise, it is just our free input text
              if (newValue?.email) setCustomerValue(newValue);
              else validateCustomerInput();
            }}
            inputValue={customerInputValue}
            onInputChange={(event, newInputValue) => {
              setCustomerInputValue(newInputValue);
            }}
            onBlur={validateCustomerInput}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer Email"
                margin="dense"
                variant="standard"
                error={!!errorText}
                helperText={errorText}
                inputProps={{ ...params.inputProps, disabled: disableInput }}
              />
            )}
          />
          {!isSkippingVehicle && (
            <>
              <Typography
                className={compClasses.sectionHeader}
                align="left"
                variant="h5"
                gutterBottom
              >
                Vehicle
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="left"
                justifyContent="center"
                width="100%"
              >
                {!newEmail && !existingCustomer && (
                  <Typography className={compClasses.sectionText}>
                    Add customer email before selecting a vehicle.
                  </Typography>
                )}

                {newEmail && <NewVehicle setVehicle={setNewVehicle} />}
                {existingCustomer && (
                  <SelectVehicle
                    vehicles={vehicles}
                    value={selectedVehicleID}
                    setValue={setSelectedVehicleID}
                    createNewVehicle={createNewVehicle}
                    setCreateNewVehicle={setCreateNewVehicle}
                    setNewVehicle={setNewVehicle}
                  />
                )}
              </Box>
            </>
          )}

          <Typography
            className={compClasses.sectionHeader}
            align="left"
            variant="h5"
            gutterBottom
          >
            Customer Needs
          </Typography>

          <Grid
            container
            direction="column"
            alignItems="flex-start"
            justify="space-around"
          >
            <TextField
              id="request-note"
              label="What services is this customer looking for..."
              placeholder="Type notes here..."
              onChange={({ target }) => setAdminNote(target.value)}
              fullWidth
              multiline
              style={{ maxWidth: '100%', margin: 'auto' }}
            />
          </Grid>
          {!isSkippingVehicle && selectedQuoteTemplateAnswers && (
            <Button
              variant="text"
              startIcon={showTemplateFields ? <RemoveIcon /> : <AddIcon />}
              onClick={() => setShowTemplateFields(!showTemplateFields)}
              style={{ marginTop: '20px' }}
            >
              {showTemplateFields ? 'Hide' : 'Show'} Tire Fields
            </Button>
          )}
          {!isSkippingVehicle &&
            selectedQuoteTemplateAnswers &&
            showTemplateFields && (
              <>
                <Typography
                  className={compClasses.sectionHeader}
                  align="left"
                  variant="h5"
                  gutterBottom
                >
                  Tire Info
                </Typography>
                <EditableQuoteAnswerList
                  answers={selectedQuoteTemplateAnswers}
                  setAnswers={setSelectedQuoteTemplateAnswers}
                  setIsValid={setAnswersValid}
                  questionType="customer"
                />
              </>
            )}
        </div>
      </MuiDialogContent>
      <MuiDialogActions>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justify="center"
          width="100%"
        >
          <Grid
            container
            direction="row"
            justify="center"
            alignItems="center"
            spacing={2}
            style={{ marginBottom: '10px' }}
          >
            {isSubmitting ? (
              <div className={compClasses.isSubmittingQuoteTextContainer}>
                <CircularProgress size={20} />
                <Typography
                  className={compClasses.isSubmittingQuoteText}
                  variant="h5"
                >
                  Creating your quote...
                </Typography>
              </div>
            ) : (
              <>
                <Grid item>
                  <Button
                    variant="contained"
                    className={classes.buttonWithoutIcon}
                    disabled={!canSubmit}
                    onClick={() => submitQuoteRequest(false)}
                  >
                    Save for later
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonWithoutIcon}
                    disabled={!canSubmit}
                    onClick={() => {
                      submitQuoteRequest(true);
                    }}
                  >
                    Continue & Build
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </MuiDialogActions>
      {showErrorMessage && (
        <MuiAlert severity="error">
          We were unable to find that vehicle. Check the license plate or VIN.
        </MuiAlert>
      )}
    </Dialog>
  );
};

export default QuickQuoteLayout;

// TODO: Button to reload data from datagrid
// TODO: Do we need 'color' as a required field in the new vehicle
